/**
 * Item Pools Store
 *
 * Manages item pools, item pool maps, and drift events.
 *
 * Lock-on-Promotion: When items are added to an Item Pool, their accepted
 * attribute values are automatically locked to prevent modification.
 * 
 * HYBRID PERSISTENCE:
 * - Primary: Supabase (synced to cloud for multi-device/multi-user access)
 * - Cache: localStorage (for offline access and quick reload)
 */

import { create } from 'zustand';
import { isPoolScoped, POOL_LOCK_REQUIRES_SCOPE_MESSAGE } from '../../lib/poolScope';
import { createItemPoolRpc, setItemPoolLock, setItemPoolCutLine } from '../../lib/governedTransactions';
import type { RationaleParts } from '../../lib/rationaleParts';
import { getReasonMessage } from '../../lib/authorization/reasonMessages';
// Removed persist - Supabase is now the primary data store
import type { ItemPool, ItemPoolMap, FocusMap, DriftEvent, PostPublishChange, ItemPoolStatus } from '../../types/prioritization';

/** Result shape for getItemPool (S2 handler): itemIds + metadata only. */
export interface GetItemPoolResult {
  itemIds: string[];
  metadata: { name: string; status: string; trackId: string; cycleId?: string };
}
export interface AddPriorityToListResult {
  success: boolean;
  reason?: string;
  blockingIssues?: Array<{ code: string; displayText: string }>;
}
import { canTransitionItemPool } from '../../types/prioritization/lifecycle';
// Import other stores for cross-store operations
import { useItemsStore } from './itemsStore';
import { useValueProposalStore } from './valueProposalStore';
import { useAttributesStore } from './attributesStore';
import { getBlockingReadinessIssues } from '../../lib/itemReadiness';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  getSafeSupabaseClient,
  createInitialSyncState,
  generateUUID,
  getCurrentWorkspaceId,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import { reconcileItemPoolMemberships } from '../../lib/itemPoolMembershipSync';
import {
  mustHave,
  hasAnyPermission,
  getStoreContext,
  PermissionDeniedError,
} from '../../lib/permissionEnforcement';
import type { PermissionAction } from '../../types/prioritization/permissions';
import { getCapabilitiesForAction } from '../../types/prioritization/capabilityIds';
import {
  checkPolicyForList,
  askAgainAfterPermissionsLoad,
} from '../../lib/authorization/storeEnforcement';
import { useUserStore } from './userStore';
import {
  retireItemPool as retireItemPoolRpc,
  restoreRetiredItemPool as restoreRetiredItemPoolRpc,
  type ReversalResult,
} from '../../lib/reversals/reversalsService';

/** A list retired through retire_item_pool (mig 758) — status 'archived'. */
export function isItemPoolRetired(pool: { status?: string | null }): boolean {
  return pool.status === 'archived';
}

const TABLE_NAME = 'item_pools';

/**
 * Read a pool's publication stamp back from the server (publish_item_pool,
 * migration 693/754, is the only writer). Null when it cannot be read.
 */
async function refreshPublicationStamp(poolId: string): Promise<{
  publishedAt?: Date;
  publishedBy?: string;
  status?: string;
  updatedAt?: Date;
} | null> {
  if (!isSupabaseAvailable()) return null;
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select('published_at, published_by, status, updated_at')
    .eq('id', poolId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as { published_at: string | null; published_by: string | null; status: string | null; updated_at: string | null };
  return {
    publishedAt: row.published_at ? new Date(row.published_at) : undefined,
    publishedBy: row.published_by ?? undefined,
    status: row.status ?? undefined,
    updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
  };
}
const DRIFT_EVENTS_TABLE = 'drift_events';

/** Monotonic fetch token — ignore stale responses from workspace/fetch races. */
let itemPoolsFetchGeneration = 0;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function toNullableActorId(value: string | undefined): string | null {
  if (!value) return null;
  return UUID_REGEX.test(value) ? value : null;
}

/** Emit item-level event to audit_events (trail of what happened to this item). Include itemId so item History shows it. */
function emitItemPoolAudit(
  eventType: string,
  payload: Record<string, unknown>,
  actorId: string | null,
  itemId?: string | null
): void {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  const now = new Date().toISOString();
  supabase.from('audit_events').insert({
    id: generateUUID(),
    event_type: eventType,
    item_id: itemId ?? null,
    payload: payload as Record<string, unknown>,
    created_by: actorId,
    created_at: now,
    workspace_id: getCurrentWorkspaceId() ?? undefined,
  }).then(({ error }) => {
    if (error) console.warn('[ItemPoolsStore] Audit event failed (non-blocking):', error.message);
  });
}

/** Emit governance event (major decision/action) to governance_events. */
function emitGovernanceEvent(
  eventType: string,
  payload: Record<string, unknown>,
  actorId: string | null,
  cycleId: string | null
): void {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  const now = new Date().toISOString();
  supabase.from('governance_events').insert({
    id: generateUUID(),
    event_type: eventType,
    occurred_at: now,
    actor_id: actorId,
    note: JSON.stringify(payload),
    cycle_id: cycleId,
    workspace_id: getCurrentWorkspaceId() ?? undefined,
  }).then(({ error }) => {
    if (error) console.warn('[ItemPoolsStore] Governance event failed (non-blocking):', error.message);
  });
}

/** Outcome of a pool write the UI must be able to report truthfully. */
export interface PoolActionResult {
  success: boolean;
  /** Plain-language reason when `success` is false. */
  error?: string;
}

interface ItemPoolsState {
  itemPools: ItemPool[];
  /**
   * Lists retired through retire_item_pool (mig 758, status 'archived'):
   * hidden from every list, kept so they can be taken back.
   */
  retiredItemPools: ItemPool[];
  itemPoolMaps: ItemPoolMap[];
  activeItemPoolId: string | null;
  driftEvents: DriftEvent[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncToSupabase: (pool: ItemPool) => Promise<{ success: boolean; error?: string }>;

  // Item pool actions
  createItemPool: (
    data: Omit<ItemPool, 'id' | 'createdAt' | 'updatedAt' | 'priorityIds' | 'isPublished' | 'version' | 'postPublishChanges' | 'hasUnacknowledgedChanges'>,
    options?: { requiredAnyOf?: PermissionAction[] },
  ) => Promise<string>;
  /**
   * Optimistic local update, then an awaited save. Resolves with the real
   * outcome (policy denial or a failed save → `success: false` + reason, and
   * the local change is rolled back) so callers never toast a save that did
   * not happen. Callers that ignore the promise keep the old behavior.
   */
  updateItemPool: (id: string, updates: Partial<ItemPool>) => Promise<PoolActionResult>;
  deleteItemPool: (id: string) => void;
  /**
   * Undo "create list": governed retire (retire_item_pool, mig 758). Reason
   * required; refused (blockedBy + remedy) once the list is locked, published
   * or used. On success the list moves to `retiredItemPools`.
   */
  retireItemPool: (id: string, reason: string) => Promise<ReversalResult>;
  /** Take back a retired list (restore_retired_item_pool, mig 758). */
  restoreRetiredItemPool: (id: string, reason: string) => Promise<ReversalResult>;
  setActiveItemPool: (id: string | null) => void;
  
  // Ranking actions
  // Reordering a PUBLISHED pool (same items, new order) is gated: without a
  // rationale nothing is applied and { requiresRationale: true } is returned;
  // with one, the reorder routes through the reorder_published_item_pool RPC
  // (migration 569), which memorializes the decision server-side (canon 27).
  updateRanking: (listId: string, priorityIds: string[], opts?: { rationale?: string }) => { requiresRationale: boolean };
  /**
   * Move the pool's cut line through set_item_pool_cut_line (migration 760):
   * the server decides who may and records the range_selection Decision.
   * A PUBLISHED pool needs a rationale — without one nothing is sent and
   * { requiresRationale: true } comes back (the server refuses the same way).
   * Local state changes only after the server accepts.
   * `rationaleParts` (985): the four-part rationale, onto that Decision; its
   * reasoning also counts as a published pool's reason.
   */
  updateCutLine: (
    listId: string,
    position: number,
    opts?: { rationale?: string; rationaleParts?: RationaleParts | null },
  ) => Promise<{ success: boolean; error?: string; requiresRationale?: boolean }>;
  addPriorityToList: (listId: string, priorityId: string, cycleId?: string, actorId?: string, selectionReason?: string, itemSource?: 'manual' | 'filter' | 'auto' | 'imported', options?: { bypassReadinessCheck?: boolean }) => AddPriorityToListResult;
  /**
   * Add many items in ONE write. Use this for any bulk add — calling
   * addPriorityToList in a loop races itself (see its implementation note).
   * Returns the ids actually added, after filtering ones already present.
   */
  addPrioritiesToList: (listId: string, priorityIds: string[], cycleId?: string, actorId?: string, selectionReason?: string, itemSource?: 'manual' | 'filter' | 'auto' | 'imported') => { added: string[]; error?: string };
  removePriorityFromList: (listId: string, priorityId: string, cycleId?: string, actorId?: string) => void;
  
  // Item pool locking (for A→P gate)
  lockItemPool: (listId: string, lockedBy: string, cycleId?: string) => Promise<boolean>;
  unlockItemPool: (listId: string, unlockedBy: string, cycleId?: string) => Promise<boolean>;
  /**
   * One governance event summarising a "Generate tracks" batch (lanes created,
   * changed, archived, skipped because locked, and per-lane errors).
   */
  recordBaselineTracksGenerated: (cycleId: string, actorId: string, summary: Record<string, unknown>) => void;
  /** Same as lockItemPool, but says why it failed. */
  lockItemPoolWithResult: (listId: string, lockedBy: string, cycleId?: string) => Promise<PoolActionResult>;
  /** Same as unlockItemPool, but says why it failed; `reason` is recorded on the ItemPoolUnlocked governance event. */
  unlockItemPoolWithResult: (listId: string, unlockedBy: string, cycleId?: string, reason?: string) => Promise<PoolActionResult>;
  isItemPoolLocked: (listId: string) => boolean;
  
  // Drift management
  addDriftEvent: (event: Omit<DriftEvent, 'id' | 'detectedAt'>) => void;
  resolveDriftEvent: (id: string, resolutionNote: string) => void;
  getDriftEventsForList: (listId: string) => DriftEvent[];
  
  // Item pool map actions
  createItemPoolMap: (data: Omit<FocusMap, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateItemPoolMap: (id: string, updates: Partial<FocusMap>) => void;
  
  // === PUBLICATION & HANDOFF ACTIONS ===
  /**
   * Record a server-confirmed publication locally. Pass
   * `{ alreadyPublished: true }` when the RPC answered `already_published`:
   * the server recorded nothing new, so the version is NOT bumped and the
   * post-publish change log is NOT cleared.
   */
  publishItemPool: (
    id: string,
    publishedBy: string,
    criteriaWeights?: Record<string, number>,
    options?: { alreadyPublished?: boolean },
  ) => void;
  handOffToPlanning: (id: string, handedOffBy: string, planningSystemRef?: string) => void;
  
  // === POST-PUBLISH DRIFT DETECTION ===
  recordPostPublishChange: (listId: string, change: Omit<PostPublishChange, 'id' | 'detectedAt'>) => void;
  acknowledgePostPublishChange: (listId: string, changeId: string, acknowledgedBy: string) => void;
  getUnacknowledgedChanges: (listId: string) => PostPublishChange[];
  
  // === CATCHBALL STATE TRANSITIONS ===
  // New 7-state model: draft → synthesized → proposed → challenged → accepted → incorporated → superseded
  synthesizeItemPool: (id: string, synthesizedBy: string) => boolean;
  proposeItemPool: (id: string, proposedBy: string, loopId?: string) => boolean;
  challengeItemPool: (id: string, challengedBy: string, note?: string) => boolean;
  acceptItemPool: (id: string, acceptedBy: string) => boolean;
  incorporateItemPool: (id: string, prioritySetId: string, incorporatedBy: string) => boolean;
  supersedeItemPool: (id: string, supersededBy: string) => boolean;
  transitionItemPoolStatus: (id: string, newStatus: ItemPoolStatus, actorId: string) => boolean;
  
  // Getters
  getItemPoolById: (id: string) => ItemPool | undefined;
  getItemPoolsByOwner: (ownerId: string) => ItemPool[];
  getActiveItemPool: () => ItemPool | undefined;
  getPublishedItemPools: () => ItemPool[];
  
  // Track Navigation (v2.2 Canon)
  /** Alias for getItemPoolById - used by sessionStore for Track navigation */
  getPool: (id: string) => ItemPool | undefined;
  /** Get all Tracks (pools where isTrack=true) for a cycle */
  getTracksForCycle: (cycleId: string) => ItemPool[];
  /** Get Tracks grouped by Track Cell (category × altitude) */
  getTracksByCell: (cycleId: string) => Map<string, ItemPool[]>;
  /** S2 handler: get item pool by id as itemIds + metadata (no full pool). */
  getItemPoolResult: (itemPoolId: string) => GetItemPoolResult | undefined;
  /** S2 handler: create item pool scoped to track (cycle + track). */
  createItemPoolForTrack: (scope: { cycleId: string; trackId: string }, name: string, options?: { ownerId?: string }) => Promise<string>;
}

/**
 * Record cycle participation for items entering a cycle through an item pool.
 *
 * Canon 03 (§Cycle Item Participation): "Adding items to a cycle
 * writes/upserts `cycle_item_participation` with inclusion metadata." That was
 * only ever true of degapProcessStore.addItemsToCycle — the Gather flow. Items
 * that reach a cycle through a cycle-bound pool, which is how the product
 * actually works now, wrote `cycle_comparison_sets` and an audit event but no
 * participation row. The table's newest row in production is 2026-07-22, while
 * workspaces created in September have none, and every item's Cycles lane on
 * the journey timeline reads empty as a result.
 *
 * Fire-and-forget, matching the membership write above it: a provenance record
 * must never block the user's add.
 */
function recordCycleParticipation(
  cycleId: string,
  itemIds: string[],
  opts: { actorId?: string | null; workspaceId?: string | null; itemSource?: 'manual' | 'filter' | 'auto' | 'imported'; note?: string | null },
): void {
  if (!cycleId || itemIds.length === 0 || !isSupabaseAvailable()) return;
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  // inclusion_reason is CHECK-constrained to
  // manual_selection | criteria_match | stakeholder_request | carryover.
  const inclusionReason =
    opts.itemSource === 'filter' || opts.itemSource === 'auto' ? 'criteria_match' : 'manual_selection';
  const now = new Date().toISOString();
  const actor = toNullableActorId(opts.actorId ?? undefined);

  const rows = itemIds.map((itemId) => ({
    cycle_id: cycleId,
    item_id: itemId,
    inclusion_reason: inclusionReason,
    inclusion_note: opts.note ?? null,
    // The item is in the cycle's comparison set, not merely nominated.
    cycle_status: 'confirmed',
    added_at: now,
    added_by: actor,
    confirmed_at: now,
    confirmed_by: actor,
    workspace_id: opts.workspaceId ?? getCurrentWorkspaceId() ?? null,
  }));

  void supabase
    .from('cycle_item_participation')
    .upsert(rows, { onConflict: 'cycle_id,item_id', ignoreDuplicates: false })
    .then(({ error }) => {
      if (error) console.warn('[ItemPoolsStore] Failed to record cycle_item_participation:', error.message);
    });
}

/** Mark participation excluded rather than deleting it — canon 03 keeps the provenance. */
function excludeCycleParticipation(cycleId: string, itemId: string, actorId?: string | null, reason?: string): void {
  if (!cycleId || !isSupabaseAvailable()) return;
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  void supabase
    .from('cycle_item_participation')
    .update({
      cycle_status: 'excluded',
      excluded_at: new Date().toISOString(),
      excluded_by: toNullableActorId(actorId ?? undefined),
      excluded_reason: reason ?? 'removed_from_pool',
    })
    .eq('cycle_id', cycleId)
    .eq('item_id', itemId)
    .then(({ error }) => {
      if (error) console.warn('[ItemPoolsStore] Failed to exclude cycle_item_participation:', error.message);
    });
}

export const useItemPoolsStore = create<ItemPoolsState>()((set, get) => ({
  itemPools: [],
  retiredItemPools: [],
  itemPoolMaps: [],
  activeItemPoolId: null,
  driftEvents: [],
  syncState: createInitialSyncState(),
  
  // ========================================================================
  // Supabase Sync Actions
  // ========================================================================
  
  fetchFromSupabase: async () => {
    if (!isSupabaseAvailable()) {
      console.log('[ItemPoolsStore] Supabase not available, using localStorage cache');
      return;
    }

    const nextGeneration = ++itemPoolsFetchGeneration;
    
    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
    
    const workspaceId = getCurrentWorkspaceId();
    const [listsResult, driftResult] = await Promise.all([
      fetchAll<ItemPool>(TABLE_NAME, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true }),
      fetchAll<DriftEvent>(DRIFT_EVENTS_TABLE, { column: 'detected_at', ascending: false }),
    ]);

    // Ignore stale responses from an earlier workspace/fetch race.
    if (nextGeneration !== itemPoolsFetchGeneration) return;
    
    if (listsResult.error || driftResult.error) {
      set({ 
        syncState: { 
          ...get().syncState, 
          isLoading: false, 
          syncError: listsResult.error || driftResult.error || 'Unknown error'
        } 
      });
      console.error('[ItemPoolsStore] Failed to fetch from Supabase:', listsResult.error || driftResult.error);
      return;
    }
    
    // MERGE strategy: preserve local-only rows for the *current* workspace that
    // aren't in Supabase yet (optimistic creates). Drop other-workspace locals
    // so workspace switches don't contaminate / flicker the list.
    const existingLists = get().itemPools;
    const existingDriftEvents = get().driftEvents;
    
    const supabaseListIds = new Set((listsResult.data || []).map(l => l.id));
    const supabaseDriftIds = new Set((driftResult.data || []).map(d => d.id));
    
    const localOnlyLists = existingLists.filter(
      (l) =>
        !supabaseListIds.has(l.id) &&
        (!workspaceId || !l.workspaceId || l.workspaceId === workspaceId),
    );
    const localOnlyDrifts = existingDriftEvents.filter(d => !supabaseDriftIds.has(d.id));
    
    const mergedLists = [...(listsResult.data || []), ...localOnlyLists].map(pool => ({
      ...pool,
      tags: Array.isArray(pool.tags) ? pool.tags : [],
      // F12: `isPublished` has no column of its own — it is what
      // `published_at` means. Without this the flag was client-memory only, so
      // after a refresh the reorder-rationale gate was disarmed and the DB
      // trigger rejected the write with a raw error toast instead.
      isPublished: pool.publishedAt != null || pool.status === 'published',
    }));
    const mergedDrifts = [...(driftResult.data || []), ...localOnlyDrifts];
    
    set({ 
      itemPools: mergedLists.filter((p) => !isItemPoolRetired(p)),
      retiredItemPools: mergedLists.filter(isItemPoolRetired),
      driftEvents: mergedDrifts,
      syncState: { 
        ...get().syncState, 
        isLoading: false, 
        lastSyncAt: new Date(),
        syncError: null 
      }
    });
    console.log(`[ItemPoolsStore] Loaded ${(listsResult.data || []).length} from Supabase, kept ${localOnlyLists.length} local-only. Total: ${mergedLists.length}`);
  },
  
  syncToSupabase: async (pool: ItemPool) => {
    if (!isSupabaseAvailable()) {
      return { success: true };
    }
    
    set({ syncState: { ...get().syncState, isSyncing: true } });
    
    const result = await updateRecord<ItemPool>(TABLE_NAME, pool.id, pool);
    
    set({ syncState: { ...get().syncState, isSyncing: false } });
    
    if (result.error) {
      console.error('[ItemPoolsStore] Failed to sync to Supabase:', result.error);
      return { success: false, error: result.error };
    }
    
    return { success: true };
  },
  
  // ========================================================================
  // Item Pool CRUD
  // ========================================================================
  
  createItemPool: async (data, options) => {
    // Standalone pools require lists.create. Track-scoped creation may pass a
    // wider any-of set (lists.edit / process.prioritize) — adding items to a
    // track should not fail only because the track's pool row does not exist yet.
    //
    // Auth check order matters: the legacy permissionEnforcement cache is often
    // empty for job-role users (e.g. priority-list-owner) even when userStore has
    // loaded RBAC / resolvedCapabilityIds. Prefer those sources first.
    const requiredAnyOf = options?.requiredAnyOf ?? (['lists.create'] as PermissionAction[]);
    const now = new Date();
    const workspaceId = getCurrentWorkspaceId() ?? (data as { workspaceId?: string }).workspaceId;
    const poolId = generateUUID();
    const provisional = {
      ...data,
      id: poolId,
      ...(workspaceId && { workspaceId }),
    } as unknown as Record<string, unknown>;

    const holdsRight = () => {
      const userState = useUserStore.getState();
      const resolvedCaps = userState.resolvedCapabilityIds ?? [];
      return (
        userState.canAny(requiredAnyOf) ||
        hasAnyPermission(requiredAnyOf, getStoreContext()) ||
        requiredAnyOf.some((action) => checkPolicyForList(action, poolId, provisional).allowed) ||
        requiredAnyOf.some((action) =>
          getCapabilitiesForAction(action).some((cap) => resolvedCaps.includes(cap)),
        )
      );
    };

    // As in createItem: a person who acts before their capabilities have arrived
    // is asked for again, not refused (the shared check loads them and re-asks).
    const allowed = await askAgainAfterPermissionsLoad(holdsRight);

    const userState = useUserStore.getState();
    if (!allowed) {
      console.warn('[ItemPoolsStore] createItemPool refused', {
        workspaceId,
        capabilities: userState.resolvedCapabilityIds.length,
      });
      throw new PermissionDeniedError(
        requiredAnyOf[0] ?? 'lists.create',
        undefined,
        `Permission denied: ${requiredAnyOf.join(' or ')}`,
      );
    }

    const newList: ItemPool = {
      ...data,
      id: poolId,
      priorityIds: [],
      cutLinePosition: 0,
      createdAt: now,
      updatedAt: now,
      status: 'draft',
      // Publication tracking defaults
      isPublished: false,
      version: 0,
      postPublishChanges: [],
      hasUnacknowledgedChanges: false,
      ...(workspaceId && { workspaceId }),
    } as ItemPool;
    
    // Update local state first (optimistic update)
    set((state) => ({
      itemPools: [...state.itemPools, newList]
    }));
    
    // One governed write (761 create_item_pool): scope + authority checks, the
    // one-live-pool-per-track refusal, the insert and the ItemPoolCreated event
    // commit together. Awaited so the pool exists before callers add items.
    if (isSupabaseAvailable()) {
      const scoped = data as { cycleId?: string | null; trackId?: string | null };
      const result = await createItemPoolRpc({
        poolId,
        name: data.name,
        workspaceId: workspaceId ?? null,
        description: data.description ?? null,
        cycleId: scoped.cycleId ?? null,
        trackId: scoped.trackId ?? null,
        tags: data.tags ?? [],
        cutLinePosition: data.cutLinePosition ?? 0,
      });
      if (!result.ok) {
        // Always roll back optimistic rows — otherwise pools "appear then vanish" on refresh.
        set((state) => ({
          itemPools: state.itemPools.filter((p) => p.id !== newList.id),
        }));
        const message = result.error ?? 'Failed to save item pool to the database.';
        console.error('[ItemPoolsStore] create_item_pool refused:', message);
        throw new Error(result.remedy ? `${message} ${result.remedy}` : message);
      }
      // The server resolves scope from the track (its cycle and workspace) and
      // makes the caller the owner — mirror what was actually saved.
      const saved = result.data ?? {};
      set((state) => ({
        itemPools: state.itemPools.map((p) =>
          p.id === newList.id
            ? ({
                ...p,
                ...(typeof saved.cycle_id === 'string' && { cycleId: saved.cycle_id }),
                ...(typeof saved.workspace_id === 'string' && { workspaceId: saved.workspace_id }),
                ...(userState.userId && { ownerId: userState.userId }),
              } as ItemPool)
            : p,
        ),
      }));
    }

    return newList.id;
  },
  
  updateItemPool: async (id, updates) => {
    const list = get().itemPools.find(l => l.id === id);
    if (!list) return { success: false, error: 'Pool not found.' };

    // Authorization guard: lists.edit (write-surfaces)
    const policyDecision = checkPolicyForList('lists.edit', id, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.edit:`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'updateItemPool', message: `Cannot update list: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return { success: false, error: getReasonMessage(reasonCode) };
    }

    const updated = { ...list, ...updates, updatedAt: new Date() };

    // Update local state first (optimistic update) — synchronous, before any await.
    set((state) => ({
      itemPools: state.itemPools.map(l => l.id === id ? updated : l)
    }));

    // Then save, and report what actually happened.
    if (!isSupabaseAvailable()) return { success: true };
    const result = await updateRecord<ItemPool>(TABLE_NAME, id, updated);
    if (result.error) {
      console.error('[ItemPoolsStore] Failed to update Item Pool in Supabase:', result.error);
      dispatchStoreError('Item Pools', 'update pool', result.error);
      // Roll back only the fields this call changed, and only if nothing newer replaced them.
      set((state) => ({
        itemPools: state.itemPools.map(l => {
          if (l.id !== id || l !== updated) return l;
          const restored = { ...l } as Record<string, unknown>;
          for (const key of Object.keys(updates)) restored[key] = (list as unknown as Record<string, unknown>)[key];
          restored.updatedAt = list.updatedAt;
          return restored as unknown as ItemPool;
        })
      }));
      return { success: false, error: result.error };
    }
    return { success: true };
  },
  
  deleteItemPool: (id) => {
    // Permission check - requires lists.edit permission (or lists.create for own lists)
    mustHave('lists.edit');
    
    // Update local state first (optimistic update)
    set((state) => ({
      itemPools: state.itemPools.filter(list => list.id !== id),
      activeItemPoolId: state.activeItemPoolId === id ? null : state.activeItemPoolId
    }));
    
    // Then sync to Supabase
    if (isSupabaseAvailable()) {
      deleteRecord(TABLE_NAME, id).then(result => {
        if (result.error) {
          console.error('[ItemPoolsStore] Failed to delete Item Pool from Supabase:', result.error);
          dispatchStoreError('Item Pools', 'delete pool', result.error);
        }
      });
    }
  },
  
  retireItemPool: async (id, reason) => {
    const result = await retireItemPoolRpc(id, reason);
    if (result.ok) {
      set((state) => {
        const pool = state.itemPools.find((p) => p.id === id);
        return {
          itemPools: state.itemPools.filter((p) => p.id !== id),
          retiredItemPools: pool
            ? [...state.retiredItemPools.filter((p) => p.id !== id), pool]
            : state.retiredItemPools,
          activeItemPoolId: state.activeItemPoolId === id ? null : state.activeItemPoolId,
        };
      });
    }
    return result;
  },

  restoreRetiredItemPool: async (id, reason) => {
    const result = await restoreRetiredItemPoolRpc(id, reason);
    if (result.ok) {
      // The DB enum carries 'active' / 'archived', which the TS ItemPoolStatus
      // union (the catchball subset) does not; keep the server's value as-is.
      const restoredStatus = (typeof result.data?.status === 'string' ? result.data.status : 'draft') as unknown as ItemPool['status'];
      set((state) => {
        const pool = state.retiredItemPools.find((p) => p.id === id);
        return {
          retiredItemPools: state.retiredItemPools.filter((p) => p.id !== id),
          itemPools: pool
            ? [...state.itemPools.filter((p) => p.id !== id), { ...pool, status: restoredStatus }]
            : state.itemPools,
        };
      });
    }
    return result;
  },

  setActiveItemPool: (id) => set({ activeItemPoolId: id }),
  
  updateRanking: (listId, priorityIds, opts) => {
    const list = get().getItemPoolById(listId);
    if (!list) return { requiresRationale: false };

    // Authorization guard: process.prioritize (write-surfaces)
    const policyDecision = checkPolicyForList('process.prioritize', listId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for process.prioritize:`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'updateRanking', message: `Cannot update ranking: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return { requiresRationale: false };
    }

    const previousOrder = list.priorityIds;
    const orderChanged = JSON.stringify(previousOrder) !== JSON.stringify(priorityIds);
    const isPurePermutation =
      orderChanged &&
      previousOrder.length === priorityIds.length &&
      JSON.stringify([...previousOrder].sort()) === JSON.stringify([...priorityIds].sort());

    // Gated path: reordering a published pool (canon 27 — no siteless
    // decisions). The DB blocks direct permutation writes (migration 569);
    // the sanctioned path is the RPC, which requires a rationale and
    // memorializes the decision.
    if (list.isPublished && isPurePermutation) {
      const rationale = opts?.rationale?.trim();
      if (!rationale) return { requiresRationale: true };

      get().recordPostPublishChange(listId, {
        type: 'priority_reordered',
        description: rationale,
        previousValue: previousOrder,
        newValue: priorityIds,
      });

      set((state) => ({
        itemPools: state.itemPools.map(l =>
          l.id === listId
            ? { ...l, priorityIds, lastRankedAt: new Date(), updatedAt: new Date() }
            : l
        )
      }));

      const supabase = getSafeSupabaseClient();
      if (supabase) {
        supabase
          .rpc('reorder_published_item_pool', {
            p_pool_id: listId,
            p_priority_ids: priorityIds,
            p_rationale: rationale,
          })
          .then(({ data, error }: { data: { success?: boolean; error?: string } | null; error: { message: string } | null }) => {
            const failure = error?.message ?? (data && data.success === false ? data.error : null);
            if (failure) {
              // Revert the optimistic reorder — the gate refused it.
              set((state) => ({
                itemPools: state.itemPools.map(l =>
                  l.id === listId ? { ...l, priorityIds: previousOrder } : l
                )
              }));
              dispatchStoreError('Item Pools', 'reorder published pool', failure);
            } else {
              void reconcileItemPoolMemberships(listId, priorityIds);
            }
          });
      }
      return { requiresRationale: false };
    }

    // Ungated path: unpublished pools, or set-changing updates on published
    // pools (those are recorded as post-publish changes).
    if (list.isPublished && orderChanged) {
      get().recordPostPublishChange(listId, {
        type: 'priority_reordered',
        description: 'Priority order was changed after publication',
        previousValue: previousOrder,
        newValue: priorityIds,
      });
    }

    set((state) => ({
      itemPools: state.itemPools.map(l =>
        l.id === listId
          ? { ...l, priorityIds, lastRankedAt: new Date(), updatedAt: new Date() }
          : l
      )
    }));

    // Sync to Supabase
    const updatedList = get().getItemPoolById(listId);
    if (updatedList) {
      get().syncToSupabase(updatedList).then(result => {
        if (!result.success && result.error) {
          dispatchStoreError('Item Pools', 'update ranking', result.error);
        } else {
          void reconcileItemPoolMemberships(listId, updatedList.priorityIds);
        }
      });
    }
    return { requiresRationale: false };
  },
  
updateCutLine: async (listId, position, opts) => {
    const list = get().getItemPoolById(listId);
    if (!list) return { success: false, error: 'List not found' };

    // Client pre-gate (the server decides; this only avoids a doomed call).
    const policyDecision = checkPolicyForList('lists.move_cutline', listId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: {
          store: 'itemPools',
          action: 'updateCutLine',
          message: `Cannot move cut line: ${reasonCode}`,
          decision: policyDecision,
        }
      }));
      return { success: false, error: `Cannot move cut line: ${reasonCode}` };
    }

    if (list.cutLinePosition === position) return { success: true };

    // Canon 27: a published pool's cut line moves only with a recorded reason.
    const rationale = opts?.rationale?.trim() || opts?.rationaleParts?.rationale_reasoning?.trim();
    if (list.isPublished && !rationale) return { success: false, requiresRationale: true };

    if (!isSupabaseAvailable()) {
      return { success: false, error: 'Cannot move the cut line while offline.' };
    }

    const result = await setItemPoolCutLine(listId, position, rationale, opts?.rationaleParts);
    if (!result.ok) {
      if (result.data?.code === 'reason_required') return { success: false, requiresRationale: true };
      return { success: false, error: result.error ?? 'The cut line could not be moved.' };
    }

    const saved = typeof result.data?.cut_line_position === 'number' ? result.data.cut_line_position : position;
    if (list.isPublished) {
      get().recordPostPublishChange(listId, {
        type: 'cutline_moved',
        description: rationale ?? `Cut line moved from position ${list.cutLinePosition} to ${saved} after publication`,
        previousValue: list.cutLinePosition,
        newValue: saved,
      });
    }
    set((state) => ({
      itemPools: state.itemPools.map(l =>
        l.id === listId ? { ...l, cutLinePosition: saved, updatedAt: new Date() } : l
      )
    }));
    return { success: true };
  },
  
  /**
   * Bulk add — one state update, one row write, one membership reconcile.
   *
   * addPriorityToList persists by firing `void syncToSupabase(updatedList)` per
   * call, and that write carries the WHOLE priority_ids array as it stood on
   * that iteration. Called in a loop over N items, N concurrent PATCHes race for
   * the same column and the last one the server applies wins — so a 21-item add
   * observed here persisted 4 items on one run and 20 on the next, with every
   * call returning success and nothing logged. The membership rows raced
   * separately, leaving priority_ids and item_pool_memberships disagreeing (20
   * vs 15). Silent partial writes, not an error path.
   *
   * Callers doing a bulk add must use this instead.
   */
  addPrioritiesToList: (listId, priorityIds, cycleId?: string, actorId?: string, selectionReason?: string, itemSource: 'manual' | 'filter' | 'auto' | 'imported' = 'manual') => {
    const list = get().getItemPoolById(listId);
    if (!list) return { added: [], error: 'List not found' };

    const listForPolicy = list.workspaceId ? list : { ...list, workspaceId: getCurrentWorkspaceId() ?? undefined };
    const policyDecision = checkPolicyForList('lists.edit', listId, listForPolicy as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      return { added: [], error: `Cannot add priorities: ${reasonCode}` };
    }

    // `present` doubles as the dedupe set, so a repeated id inside the incoming
    // list is added once rather than twice.
    const present = new Set(list.priorityIds);
    const added: string[] = [];
    for (const id of priorityIds) {
      if (!id || present.has(id)) continue;
      present.add(id);
      added.push(id);
    }
    if (added.length === 0) return { added: [] };

    const now = new Date();
    const nextIds = [...list.priorityIds, ...added];

    set((state) => ({
      itemPools: state.itemPools.map((l) =>
        l.id === listId ? { ...l, priorityIds: nextIds, updatedAt: now } : l,
      ),
    }));

    const updatedList = get().getItemPoolById(listId);
    if (isSupabaseAvailable() && updatedList) {
      void get().syncToSupabase(updatedList).then(() => {
        void reconcileItemPoolMemberships(listId, updatedList.priorityIds, {
          actorId: actorId || list.ownerId,
        });
      });
    }

    if (cycleId && isSupabaseAvailable()) {
      emitItemPoolAudit(
        'ItemPoolItemAdded',
        {
          itemPoolId: listId,
          cycleId,
          action: 'add',
          itemIds: added,
          selectionReason: selectionReason || 'Bulk add',
          itemSource,
          newPoolSize: nextIds.length,
        },
        toNullableActorId(actorId || list.ownerId),
        null,
      );
    }

    // Provenance for every item in the batch, one write.
    if (cycleId) {
      recordCycleParticipation(cycleId, added, {
        actorId: actorId || list.ownerId,
        workspaceId: list.workspaceId ?? null,
        itemSource,
        note: selectionReason ?? null,
      });
    }

    return { added };
  },

  addPriorityToList: (listId, priorityId, cycleId?: string, actorId?: string, selectionReason?: string, itemSource: 'manual' | 'filter' | 'auto' | 'imported' = 'manual', options?: { bypassReadinessCheck?: boolean }) => {
    const list = get().getItemPoolById(listId);
    if (!list) return { success: false, reason: 'List not found' };
    // Policy needs workspaceId; use current workspace when list has none (e.g. legacy or not yet synced)
    const listForPolicy = list.workspaceId ? list : { ...list, workspaceId: getCurrentWorkspaceId() ?? undefined };
    // Authorization guard: lists.edit / process.prioritize (write-surfaces)
    const policyDecision = checkPolicyForList('lists.edit', listId, listForPolicy as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.edit (addPriorityToList):`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'addPriorityToList', message: `Cannot add priority: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return { success: false, reason: `Cannot add priority: ${reasonCode}` };
    }

    const item = useItemsStore.getState().getItemById(priorityId);
    if (!item) {
      return { success: false, reason: 'Item not found' };
    }

    // Skip readiness check for track pools: Values step comes after Item Pool in track workflow
    const requiredAttributes = useAttributesStore.getState().getRequiredAttributes();
    if (!options?.bypassReadinessCheck) {
      const blockingIssues = getBlockingReadinessIssues(item, requiredAttributes);
      if (blockingIssues.length > 0) {
        return {
          success: false,
          reason: 'Item is missing required prioritization inputs',
          blockingIssues: blockingIssues.map((issue) => ({
            code: issue.code,
            displayText: issue.displayText,
          })),
        };
      }
    }
    
    const now = new Date();
    
    // Check if this is a change to a published list
    if (list.isPublished && !list.priorityIds.includes(priorityId)) {
      get().recordPostPublishChange(listId, {
        type: 'priority_added',
        description: `New priority added to published list`,
        affectedPriorityIds: [priorityId],
        newValue: priorityId,
      });
    }
    
    // Lock accepted values when adding to Item Pool
    try {
      const valueProposalStore = useValueProposalStore.getState();
      
      // Lock each required attribute's value for this item
      requiredAttributes.forEach(attr => {
        // Check if there's an accepted value for this attribute
        const hasAcceptedValue = valueProposalStore.acceptedValues.some(
          v => v.itemId === priorityId && v.attributeKey === attr.key
        );
        
        if (hasAcceptedValue) {
          valueProposalStore.lockValue(priorityId, attr.key, actorId || list.ownerId, cycleId, { silent: true });
        }
      });
    } catch (error) {
      console.warn('Could not lock values on Item Pool promotion:', error);
      // Don't block the add operation if locking fails
    }
    
    // Add to list
    set((state) => ({
      itemPools: state.itemPools.map(l => {
        if (l.id === listId && !l.priorityIds.includes(priorityId)) {
          return {
            ...l,
            priorityIds: [...l.priorityIds, priorityId],
            updatedAt: now
          };
        }
        return l;
      })
    }));
    
    // Sync to Supabase + membership row
    const updatedList = get().getItemPoolById(listId);

    // Persist membership + reconcile ranks (non-blocking).
    if (isSupabaseAvailable() && updatedList) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        void get().syncToSupabase(updatedList).then(() => {
          void supabase
            .from('item_pool_memberships')
            .upsert(
              {
                item_pool_id: listId,
                item_id: priorityId,
                rank: updatedList.priorityIds.indexOf(priorityId) + 1,
                added_at: now.toISOString(),
                added_by: actorId || list.ownerId,
                selection_reason: selectionReason || null,
                item_source: itemSource,
              },
              { onConflict: 'item_pool_id,item_id' },
            )
            .then(({ error }) => {
              if (error) {
                console.warn('[ItemPoolsStore] Failed to persist item_pool_membership:', error.message);
              }
              void reconcileItemPoolMemberships(listId, updatedList.priorityIds, {
                actorId: actorId || list.ownerId,
              });
            });
        });
      }
    }

    // Do not emit ItemPoolItemAdded with item_id: it would clutter the item's History tab with low-value "added to pool" entries.
    if (cycleId && isSupabaseAvailable()) {
      emitItemPoolAudit(
        'ItemPoolItemAdded',
        {
          itemPoolId: listId,
          cycleId,
          action: 'add',
          itemId: priorityId,
          selectionReason: selectionReason || 'No reason provided',
          itemSource: itemSource,
          newPoolSize: updatedList?.priorityIds.length ?? 0,
        },
        toNullableActorId(actorId || list.ownerId),
        null
      );
    }
    // Provenance: the item is now in this cycle (canon 03 §Cycle Item Participation).
    if (cycleId) {
      recordCycleParticipation(cycleId, [priorityId], {
        actorId: actorId || list.ownerId,
        workspaceId: list.workspaceId ?? null,
        itemSource,
        note: selectionReason ?? null,
      });
    }
    if (cycleId && isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        // Sync to cycle_comparison_sets.item_ids
        if (updatedList) {
          supabase.from('cycle_comparison_sets').upsert({
            cycle_id: cycleId,
            item_ids: updatedList.priorityIds,
            updated_at: now.toISOString(),
          }, { onConflict: 'cycle_id' }).then(({ error }) => {
            if (error) {
              console.warn('[ItemPoolsStore] Failed to sync to cycle_comparison_sets:', error.message);
            }
          });
        }
      }
    }
    return { success: true };
  },
  
  removePriorityFromList: (listId, priorityId, cycleId?, actorId?) => {
    const list = get().getItemPoolById(listId);
    if (!list) return;
    
    // Authorization guard: lists.edit (write-surfaces)
    const policyDecision = checkPolicyForList('lists.edit', listId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.edit (removePriorityFromList):`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'removePriorityFromList', message: `Cannot remove priority: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return;
    }
    
    const now = new Date();
    
    // Check if this is a change to a published list
    if (list.isPublished && list.priorityIds.includes(priorityId)) {
      get().recordPostPublishChange(listId, {
        type: 'priority_removed',
        description: `Priority removed from published list`,
        affectedPriorityIds: [priorityId],
        previousValue: priorityId,
      });
    }
    
    set((state) => ({
      itemPools: state.itemPools.map(l => {
        if (l.id === listId) {
          return {
            ...l,
            priorityIds: l.priorityIds.filter(id => id !== priorityId),
            updatedAt: now
          };
        }
        return l;
      })
    }));
    
    // Sync to Supabase and drop orphan membership rows
    const updatedList = get().getItemPoolById(listId);
    if (updatedList) {
      get().syncToSupabase(updatedList);
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        void supabase
          .from('item_pool_memberships')
          .delete()
          .eq('item_pool_id', listId)
          .eq('item_id', priorityId)
          .then(() =>
            reconcileItemPoolMemberships(listId, updatedList.priorityIds, {
              actorId: actorId || list.ownerId,
            }),
          );
      }
    }

    if (cycleId && isSupabaseAvailable()) {
      emitItemPoolAudit(
        'ItemPoolUpdated',
        {
          itemPoolId: listId,
          cycleId,
          action: 'remove',
          itemId: priorityId,
          newPoolSize: (updatedList?.priorityIds.length || 0),
        },
        toNullableActorId(actorId || list.ownerId),
        priorityId
      );
      const supabase = getSafeSupabaseClient();
      if (supabase) {

        // Sync to cycle_comparison_sets.item_ids
        if (updatedList) {
          supabase.from('cycle_comparison_sets').upsert({
            cycle_id: cycleId,
            item_ids: updatedList.priorityIds,
            updated_at: now.toISOString(),
          }, { onConflict: 'cycle_id' }).then(({ error }) => {
            if (error) {
              console.warn('[ItemPoolsStore] Failed to sync to cycle_comparison_sets:', error.message);
            }
          });
        }
      }
    }

    // Keep the provenance: the row is marked excluded, never deleted.
    if (cycleId) excludeCycleParticipation(cycleId, priorityId, actorId);
  },

  /**
   * Lock the Item Pool for ranking
   * Per Doc 04: Item Pools must be locked before ranking
   */
  lockItemPool: async (listId, lockedBy, cycleId?) =>
    (await get().lockItemPoolWithResult(listId, lockedBy, cycleId)).success,

  unlockItemPool: async (listId, unlockedBy, cycleId?) =>
    (await get().unlockItemPoolWithResult(listId, unlockedBy, cycleId)).success,

  recordBaselineTracksGenerated: (cycleId, actorId, summary) => {
    if (!isSupabaseAvailable()) return;
    emitGovernanceEvent('BaselineTracksGenerated', { cycleId, ...summary }, toNullableActorId(actorId), cycleId);
  },

  /**
   * Lock / unlock go through ONE governed RPC (migration 749,
   * set_item_pool_lock): the server decides who may, writes pool_locked_at/by
   * and the ItemPoolLocked / ItemPoolUnlocked governance event in the same
   * transaction. Nothing changes on screen until the server says it happened;
   * a refusal comes back as the server's own words.
   */
  lockItemPoolWithResult: async (listId, _lockedBy, cycleId?) => {
    const list = get().getItemPoolById(listId);
    if (!list) return { success: false, error: 'Pool not found.' };
    // Doc 03 §2.11A: scope is required at lock (the server refuses too; this
    // saves the round trip and keeps the one message).
    if (!isPoolScoped({ cycleId: list.cycleId ?? cycleId, trackId: list.trackId })) {
      return { success: false, error: POOL_LOCK_REQUIRES_SCOPE_MESSAGE };
    }
    const result = await setItemPoolLock(listId, true);
    if (!result.ok) return { success: false, error: result.error ?? 'The lock could not be saved. The pool is still open.' };
    const lockedAt = typeof result.data?.pool_locked_at === 'string' ? new Date(result.data.pool_locked_at) : new Date();
    const lockedBy = typeof result.data?.pool_locked_by === 'string' ? result.data.pool_locked_by : list.poolLockedBy;
    set((state) => ({
      itemPools: state.itemPools.map((l) =>
        l.id === listId ? { ...l, poolLockedAt: lockedAt, poolLockedBy: lockedBy, updatedAt: new Date() } : l,
      ),
    }));
    return { success: true };
  },

  unlockItemPoolWithResult: async (listId, _unlockedBy, _cycleId?, reason?) => {
    const list = get().getItemPoolById(listId);
    if (!list) return { success: false, error: 'Pool not found.' };
    const result = await setItemPoolLock(listId, false, reason);
    if (!result.ok) {
      const message = result.error ?? 'The unlock could not be saved. The pool is still locked.';
      return { success: false, error: result.remedy ? `${message} ${result.remedy}` : message };
    }
    set((state) => ({
      itemPools: state.itemPools.map((l) =>
        l.id === listId
          ? { ...l, poolLockedAt: null as unknown as undefined, poolLockedBy: null as unknown as undefined, updatedAt: new Date() }
          : l,
      ),
    }));
    return { success: true };
  },

  isItemPoolLocked: (listId) => {
    const list = get().getItemPoolById(listId);
    if (!list) return false;
    const raw = list as ItemPool & { pool_locked_at?: string | Date };
    return !!(raw.poolLockedAt ?? raw.pool_locked_at);
  },
  
  addDriftEvent: (eventData) => {
    const poolId = eventData.itemPoolId;
    if (!poolId) return;
    const list = get().getItemPoolById(poolId);
    if (!list) return;
    const policyDecision = checkPolicyForList('lists.edit', poolId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemPoolsStore] Policy denied for lists.edit (addDriftEvent):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'itemPools', action: 'addDriftEvent', message: `Cannot add drift event: ${reasonCode}`, decision: policyDecision },
      }));
      return;
    }
    const newEvent: DriftEvent = {
      ...eventData,
      id: generateUUID(),
      itemPoolId: poolId,
      detectedAt: new Date(),
      resolved: false
    };
    
    set((state) => ({
      driftEvents: [...state.driftEvents, newEvent]
    }));
    
    // Sync drift event to Supabase
    if (isSupabaseAvailable()) {
      insertRecord(DRIFT_EVENTS_TABLE, newEvent).then(result => {
        if (result.error) {
          console.error('[ItemPoolsStore] Failed to insert drift event to Supabase:', result.error);
        }
      });
    }
  },
  
  resolveDriftEvent: (id, resolutionNote) => {
    const event = get().driftEvents.find(e => e.id === id);
    const poolId = event?.itemPoolId;
    if (event && poolId) {
      const list = get().getItemPoolById(poolId);
      if (list) {
        const policyDecision = checkPolicyForList('lists.edit', poolId, list as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[ItemPoolsStore] Policy denied for lists.edit (resolveDriftEvent):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'itemPools', action: 'resolveDriftEvent', message: `Cannot resolve drift: ${reasonCode}`, decision: policyDecision },
          }));
          return;
        }
      }
    }
    set((state) => ({
      driftEvents: state.driftEvents.map(ev =>
        ev.id === id 
          ? { ...ev, resolved: true, resolvedAt: new Date(), resolutionNote }
          : ev
      )
    }));
    
    // Sync to Supabase
    if (isSupabaseAvailable()) {
      updateRecord(DRIFT_EVENTS_TABLE, id, { resolved: true, resolvedAt: new Date(), resolutionNote });
    }
  },
  
  getDriftEventsForList: (listId) => 
    get().driftEvents.filter(event => event.itemPoolId === listId && !event.resolved),
  
  createItemPoolMap: (data) => {
    const newMap: FocusMap = {
      ...data,
      id: generateUUID(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // Authorization guard: lists.create (write-surfaces)
    const policyDecision = checkPolicyForList('lists.create', newMap.id, newMap as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.create (createItemPoolMap):`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'createItemPoolMap', message: `Cannot create focus map: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return '';
    }
    
    set((state) => ({
      itemPoolMaps: [...state.itemPoolMaps, newMap]
    }));
    
    return newMap.id;
  },
  
  updateItemPoolMap: (id, updates) => {
    const map = get().itemPoolMaps.find(m => m.id === id);
    if (!map) return;
    // Authorization guard: lists.edit (write-surfaces)
    const policyDecision = checkPolicyForList('lists.edit', id, map as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.edit (updateItemPoolMap):`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'itemPools', action: 'updateItemPoolMap', message: `Cannot update focus map: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return;
    }
    set((state) => ({
      itemPoolMaps: state.itemPoolMaps.map(m =>
        m.id === id ? { ...m, ...updates, updatedAt: new Date() } : m
      )
    }));
  },
  
  // ========================================================================
  // Publication & Handoff Actions
  // ========================================================================
  
  publishItemPool: (id, publishedBy, criteriaWeights, options) => {
    const list = get().getItemPoolById(id);
    if (!list) return;

    // The server said `already_published`: it recorded nothing new. Until a
    // server-side re-publish exists, the published version is unchanged — do
    // not bump the version and do not clear the change log. Only make sure the
    // local copy agrees that the pool IS published (the stamp lives server-side).
    if (options?.alreadyPublished) {
      if (!list.isPublished) {
        set((state) => ({
          itemPools: state.itemPools.map(l =>
            l.id === id ? ({ ...l, isPublished: true, status: 'published' } as unknown as ItemPool) : l
          )
        }));
      }
      return;
    }
    
    // Policy check for publishing
    const policyDecision = checkPolicyForList('lists.publish', id, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemPoolsStore] Policy denied for lists.publish:`, reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', { 
        detail: { 
          store: 'itemPools', 
          action: 'publishItemPool', 
          message: `Cannot publish list: ${reasonCode}`,
          decision: policyDecision,
        } 
      }));
      return;
    }

    const newVersion = (list.version || 0) + 1;

    set((state) => ({
      itemPools: state.itemPools.map(l =>
        l.id === id
          ? {
              ...l,
              isPublished: true,
              publishedAt: new Date(),
              publishedBy,
              version: newVersion,
              status: 'published' as const,
              criteriaWeightsSnapshot: criteriaWeights || l.criteriaWeightsSnapshot,
              // Reset post-publish changes on new publish
              postPublishChanges: [],
              hasUnacknowledgedChanges: false,
              updatedAt: new Date(),
            }
          : l
      )
    }));
    
    // No client write here. publish_item_pool already stamped published_at /
    // published_by / status (and the governance event) in one transaction —
    // the full-row sync that used to follow it was a second, ungoverned write
    // (it overwrote the server's timestamp with the browser clock). The local
    // version / change log / criteria-weight snapshot have no column. Refresh
    // the publication stamp from the server instead.
    void refreshPublicationStamp(id).then((stamp) => {
      if (!stamp) return;
      set((state) => ({
        itemPools: state.itemPools.map((l) =>
          l.id === id
            ? ({
                ...l,
                publishedAt: stamp.publishedAt ?? l.publishedAt,
                publishedBy: stamp.publishedBy ?? l.publishedBy,
                status: (stamp.status ?? l.status) as ItemPool['status'],
                isPublished: stamp.publishedAt != null || l.isPublished,
                updatedAt: stamp.updatedAt ?? l.updatedAt,
              } as ItemPool)
            : l,
        ),
      }));
    });

    console.log(`[ItemPoolsStore] Published Item Pool "${list.name}" (version ${newVersion})`);
  },
  
  handOffToPlanning: (id, handedOffBy, planningSystemRef) => {
    const list = get().getItemPoolById(id);
    if (!list) return;
    const policyDecision = checkPolicyForList('lists.edit', id, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemPoolsStore] Policy denied for lists.edit (handOffToPlanning):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'itemPools', action: 'handOffToPlanning', message: `Cannot hand off: ${reasonCode}`, decision: policyDecision },
      }));
      return;
    }
    set((state) => ({
      itemPools: state.itemPools.map(l => 
        l.id === id
          ? {
              ...l,
              handedOffToPlanningAt: new Date(),
              handedOffToPlanningBy: handedOffBy,
              planningSystemRef,
              updatedAt: new Date(),
            }
          : l
      )
    }));
    
    // Sync to Supabase
    const updatedList = get().getItemPoolById(id);
    if (updatedList) get().syncToSupabase(updatedList);
    
    console.log(`[ItemPoolsStore] Handed off Item Pool "${list.name}" to planning`);
  },
  
  // ========================================================================
  // Post-Publish Drift Detection
  // ========================================================================
  
  recordPostPublishChange: (listId, change) => {
    const list = get().getItemPoolById(listId);
    if (!list || !list.isPublished) return; // Only track changes for published lists
    const policyDecision = checkPolicyForList('lists.edit', listId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemPoolsStore] Policy denied for lists.edit (recordPostPublishChange):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'itemPools', action: 'recordPostPublishChange', message: `Cannot record change: ${reasonCode}`, decision: policyDecision },
      }));
      return;
    }
    const newChange: PostPublishChange = {
      ...change,
      id: generateUUID(),
      detectedAt: new Date(),
    };
    
    set((state) => ({
      itemPools: state.itemPools.map(l => 
        l.id === listId
          ? {
              ...l,
              postPublishChanges: [...(l.postPublishChanges || []), newChange],
              hasUnacknowledgedChanges: true,
              updatedAt: new Date(),
            }
          : l
      )
    }));
    
    // Sync to Supabase
    const updatedList = get().getItemPoolById(listId);
    if (updatedList) get().syncToSupabase(updatedList);
    
    console.log(`[ItemPoolsStore] Recorded post-publish change for "${list.name}": ${change.type}`);
  },
  
  acknowledgePostPublishChange: (listId, changeId, acknowledgedBy) => {
    const list = get().getItemPoolById(listId);
    if (!list) return;
    const policyDecision = checkPolicyForList('lists.edit', listId, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemPoolsStore] Policy denied for lists.edit (acknowledgePostPublishChange):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'itemPools', action: 'acknowledgePostPublishChange', message: `Cannot acknowledge: ${reasonCode}`, decision: policyDecision },
      }));
      return;
    }
    set((state) => ({
      itemPools: state.itemPools.map(l => {
        if (l.id !== listId) return l;
        
        const updatedChanges = (l.postPublishChanges || []).map(c =>
          c.id === changeId
            ? { ...c, acknowledgedAt: new Date(), acknowledgedBy }
            : c
        );
        
        const hasUnacknowledged = updatedChanges.some(c => !c.acknowledgedAt);
        
        return {
          ...l,
          postPublishChanges: updatedChanges,
          hasUnacknowledgedChanges: hasUnacknowledged,
          updatedAt: new Date(),
        };
      })
    }));
    
    // Sync to Supabase
    const updatedList = get().getItemPoolById(listId);
    if (updatedList) get().syncToSupabase(updatedList);
  },
  
  getUnacknowledgedChanges: (listId) => {
    const list = get().getItemPoolById(listId);
    if (!list) return [];
    return (list.postPublishChanges || []).filter(c => !c.acknowledgedAt);
  },
  
  // Getters
  getItemPoolById: (id) => get().itemPools.find(list => list.id === id),
  
  getItemPoolsByOwner: (ownerId) => 
    get().itemPools.filter(list => list.ownerId === ownerId),
  
  getActiveItemPool: () => {
    const { activeItemPoolId, itemPools } = get();
    return activeItemPoolId ? itemPools.find(list => list.id === activeItemPoolId) : undefined;
  },
  
  getPublishedItemPools: () => get().itemPools.filter(list => list.isPublished),
  
  // ========================================================================
  // Track Navigation (v2.2 Canon)
  // ========================================================================
  
  getPool: (id) => get().itemPools.find(pool => pool.id === id),
  
  getTracksForCycle: (cycleId) => {
    return get().itemPools.filter(pool => 
      pool.cycleId === cycleId && 
      pool.isTrack === true &&
      pool.requiredItemCategory != null &&
      pool.requiredAltitudeLevel != null
    );
  },
  
  getTracksByCell: (cycleId) => {
    const tracks = get().getTracksForCycle(cycleId);
    const cellMap = new Map<string, ItemPool[]>();
    
    for (const track of tracks) {
      const cellKey = `${track.requiredItemCategory}:${track.requiredAltitudeLevel}`;
      const existing = cellMap.get(cellKey) || [];
      cellMap.set(cellKey, [...existing, track]);
    }
    
    return cellMap;
  },

  getItemPoolResult: (itemPoolId) => {
    const pool = get().getPool(itemPoolId);
    if (!pool) return undefined;
    return {
      itemIds: pool.priorityIds ?? [],
      metadata: {
        name: pool.name,
        status: pool.status,
        trackId: pool.trackId,
        cycleId: pool.cycleId,
      },
    };
  },

  createItemPoolForTrack: async (scope, name, options) => {
    return await get().createItemPool(
      {
        name,
        ownerId: options?.ownerId ?? 'system',
        comparisonSetId: '',
        criteriaIds: [],
        cutLinePosition: 0,
        status: 'draft',
        tags: [],
        trackId: scope.trackId,
        cycleId: scope.cycleId,
      },
      {
        // Auto-creating the track's pool is part of managing the track item pool,
        // not creating a standalone focus list — accept edit/prioritize as well.
        requiredAnyOf: ['lists.create', 'lists.edit', 'process.prioritize', 'process.edit'],
      },
    );
  },

  // ========================================================================
  // Catchball State Transitions
  // New 7-state model: draft → synthesized → proposed → challenged → accepted → incorporated → superseded
  // ========================================================================
  
  transitionItemPoolStatus: (id, newStatus, _actorId) => {
    const list = get().getItemPoolById(id);
    if (!list) {
      console.error('[ItemPoolsStore] List not found:', id);
      return false;
    }
    const policyDecision = checkPolicyForList('lists.edit', id, list as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemPoolsStore] Policy denied for lists.edit (transitionItemPoolStatus):', reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: { store: 'itemPools', action: 'transitionItemPoolStatus', message: `Cannot transition status: ${reasonCode}`, decision: policyDecision },
      }));
      return false;
    }
    const currentStatus = list.status as ItemPoolStatus;
    if (!canTransitionItemPool(currentStatus, newStatus)) {
      console.error(`[ItemPoolsStore] Invalid transition from ${currentStatus} to ${newStatus}`);
      return false;
    }
    
    const updated = {
      ...list,
      status: newStatus,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      itemPools: state.itemPools.map(l => l.id === id ? updated : l)
    }));
    
    // Sync to Supabase
    get().syncToSupabase(updated);
    
    console.log(`[ItemPoolsStore] Transitioned "${list.name}" from ${currentStatus} to ${newStatus}`);
    return true;
  },
  
  synthesizeItemPool: (id, synthesizedBy) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'synthesized', synthesizedBy);
    if (success) {
      console.log(`[ItemPoolsStore] Synthesized Item Pool "${list.name}"`);
    }
    return success;
  },
  
  proposeItemPool: (id, proposedBy, loopId) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'proposed', proposedBy);
    if (success) {
      // Optionally track the loop association
      if (loopId) {
        set((state) => ({
          itemPools: state.itemPools.map(l => 
            l.id === id ? { ...l, catchballLoopId: loopId } : l
          )
        }));
      }
      console.log(`[ItemPoolsStore] Proposed Item Pool "${list.name}"${loopId ? ` for loop ${loopId}` : ''}`);
    }
    return success;
  },
  
  challengeItemPool: (id, challengedBy, note) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'challenged', challengedBy);
    if (success && note) {
      // Record the challenge note
      set((state) => ({
        itemPools: state.itemPools.map(l => 
          l.id === id ? { 
            ...l, 
            challengeNote: note,
            challengedAt: new Date(),
            challengedBy,
          } : l
        )
      }));
      console.log(`[ItemPoolsStore] Challenged Item Pool "${list.name}": ${note}`);
    }
    return success;
  },
  
  acceptItemPool: (id, acceptedBy) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'accepted', acceptedBy);
    if (success) {
      console.log(`[ItemPoolsStore] Accepted Item Pool "${list.name}"`);
    }
    return success;
  },
  
  incorporateItemPool: (id, prioritySetId, incorporatedBy) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'incorporated', incorporatedBy);
    if (success) {
      // Track the priority list association
      set((state) => ({
        itemPools: state.itemPools.map(l => 
          l.id === id ? { ...l, prioritySetId, incorporatedAt: new Date() } : l
        )
      }));
      console.log(`[ItemPoolsStore] Incorporated Item Pool "${list.name}" into Priority List ${prioritySetId}`);
    }
    return success;
  },
  
  supersedeItemPool: (id, supersededBy) => {
    const list = get().getItemPoolById(id);
    if (!list) return false;
    
    const success = get().transitionItemPoolStatus(id, 'superseded', supersededBy);
    if (success) {
      console.log(`[ItemPoolsStore] Superseded Item Pool "${list.name}"`);
    }
    return success;
  },
    }));

